// client/src/coms/stickers/stickerSuggest.js
// Predictive sticker suggestions (LINE-style): find the word the user just typed, match it against sticker
// keywords from every pack they can use, and remove the trigger word once a sticker is sent.
// Everything here runs locally; the sticker list is fetched once and cached (see loadSuggestionPacks).
import { comsApiGet } from '../comsApi';
import { localUsablePacks } from './localStickerStore';

export const MAX_SUGGESTIONS = 30;
export const MIN_PREFIX = 2;
export const STICKERS_CHANGED_EVENT = 'vox-stickers-changed';
const CACHE_MS = 2 * 60 * 1000;

const WORD_CHAR = /[\p{L}\p{N}\p{M}_'’-]/u;
// Same rules as the server (routes/comms/stickers.js).
const KEYWORD_RE = /^[\p{L}\p{N}](?:[\p{L}\p{N}\p{M}_'-]*[\p{L}\p{N}\p{M}])?$/u;

export function normalizeKeyword(v) {
  const k = String(v == null ? '' : v)
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[‘’]/g, "'")
    .trim()
    .replace(/^#+/, '');
  return k.length <= 32 && KEYWORD_RE.test(k) ? k : '';
}

// The word that ends exactly at the caret, or null. Not inside mentions (@name), emoji shortcodes (:name),
// tokens (<...>), commands or URLs, or inline code.
export function wordAtCaret(text, caret) {
  const s = String(text == null ? '' : text);
  const pos = Math.max(0, Math.min(Number.isFinite(caret) ? caret : s.length, s.length));
  if (pos < s.length && WORD_CHAR.test(s[pos])) return null; // caret in the middle of a word
  let start = pos;
  while (start > 0 && WORD_CHAR.test(s[start - 1])) start -= 1;
  let raw = s.slice(start, pos);
  // Trailing apostrophes/hyphens are not part of the word ("lol-" -> "lol").
  const trimmed = raw.replace(/['’-]+$/, '');
  const end = start + trimmed.length;
  raw = trimmed.replace(/^['’-]+/, '');
  const wordStart = end - raw.length;
  if (!raw) return null;
  const before = wordStart > 0 ? s[wordStart - 1] : '';
  if ('@:<#/\\'.includes(before) && before !== '') return null;
  const chunkStart = s.lastIndexOf(' ', wordStart - 1) + 1;
  const chunk = s.slice(chunkStart, end).toLowerCase();
  if (chunk.includes('://') || chunk.startsWith('www.') || chunk.includes('<')) return null;
  const ticks = (s.slice(0, wordStart).match(/`/g) || []).length;
  if (ticks % 2 === 1) return null;
  const word = normalizeKeyword(raw);
  return word ? { word, raw, start: wordStart, end } : null;
}

// Stickers this channel allows: this server's own packs need Send stickers, everything else (personal,
// installed and other servers' packs) needs Use external stickers. The server enforces the same rule.
// Packs from /stickers/usable carry `via` ('server': owned by or enabled for this server; 'personal').
export function usableStickers(packs, { serverKey = '', canSendServer = true, canSendExternal = true } = {}) {
  const out = [];
  let order = 0;
  for (const p of packs || []) {
    const own = p.via ? p.via === 'server' : p.visibility === 'server' && serverKey && String(p.server_key || '') === String(serverKey);
    if (own ? !canSendServer : !canSendExternal) continue;
    for (const s of p.stickers || []) {
      const keywords = (s.keywords || []).map(normalizeKeyword).filter(Boolean);
      if (keywords.length) out.push({ ...s, keywords, packId: p.id, packName: p.name, order: order++ });
    }
  }
  return out;
}

// Exact keyword matches first, then prefixes (shortest keyword first), favourites before others.
export function suggestStickers(index, word, max = MAX_SUGGESTIONS) {
  const q = normalizeKeyword(word);
  if (!q) return [];
  const seen = new Set();
  const scored = [];
  for (const s of index || []) {
    if (seen.has(s.id)) continue;
    let rank = -1;
    let len = Infinity;
    for (const k of s.keywords) {
      if (k === q) {
        rank = 0;
        len = 0;
        break;
      }
      if (q.length >= MIN_PREFIX && k.startsWith(q) && k.length < len) {
        rank = 1;
        len = k.length;
      }
    }
    if (rank < 0) continue;
    seen.add(s.id);
    scored.push({ s, rank, len });
  }
  scored.sort((a, b) => a.rank - b.rank || a.len - b.len || (b.s.favorite ? 1 : 0) - (a.s.favorite ? 1 : 0) || a.s.order - b.s.order);
  return scored.slice(0, max).map((x) => x.s);
}

// Removes the trigger word (and one neighbouring space) and keeps everything else the user typed.
// Returns { text, caret }; text is unchanged when the word is no longer there.
export function removeTriggerWord(text, target) {
  const s = String(text == null ? '' : text);
  if (!target || !target.word) return { text: s, caret: s.length };
  const isBoundary = (i) => i <= 0 || i >= s.length || !WORD_CHAR.test(s[i]);
  const matchesAt = (i) => normalizeKeyword(s.slice(i, i + target.raw.length)) === target.word && isBoundary(i - 1) && isBoundary(i + target.raw.length);
  let start = -1;
  if (Number.isInteger(target.start) && matchesAt(target.start)) start = target.start;
  else {
    // The draft changed while the sticker was sending: use the last whole-word occurrence, if any.
    for (let i = s.length - target.raw.length; i >= 0; i -= 1) {
      if (matchesAt(i)) {
        start = i;
        break;
      }
    }
  }
  if (start < 0) return { text: s, caret: s.length };
  let a = start;
  let b = start + target.raw.length;
  if (s[b] === ' ') b += 1;
  else if (a > 0 && s[a - 1] === ' ') a -= 1;
  const next = s.slice(0, a) + s.slice(b);
  return next.trim() ? { text: next, caret: a } : { text: '', caret: 0 };
}

// ---- stickers usable in a conversation (server decides; one request per context, cached) ----
// serverKey '' = a DM (personal packs only). The device's local packs are added after the server's
// (they are personal: in server channels they need Use external stickers).
const cache = new Map(); // context -> { at, promise }

export function loadSuggestionPacks({ serverKey = '', force = false } = {}) {
  const key = String(serverKey || '');
  const hit = cache.get(key);
  if (!force && hit && Date.now() - hit.at < CACHE_MS) return hit.promise;
  const server = comsApiGet(`/stickers/usable${key ? `?server=${encodeURIComponent(key)}` : ''}`)
    .then((r) => (Array.isArray(r && r.packs) ? r.packs : []))
    .catch(() => {
      cache.delete(key); // retry next time
      return [];
    });
  const promise = Promise.all([server, localUsablePacks().catch(() => [])]).then(([remote, local]) => [...remote, ...local]);
  cache.set(key, { at: Date.now(), promise });
  return promise;
}

export function invalidateSuggestionPacks() {
  cache.clear();
}

export function notifyStickersChanged() {
  invalidateSuggestionPacks();
  try {
    window.dispatchEvent(new CustomEvent(STICKERS_CHANGED_EVENT));
  } catch {}
}
